import React, { useState, useEffect } from 'react';
import { ShoppingBag, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MerchZone = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/merch`);
      const data = await response.json();
      if (data.products) {
        setProducts(data.products);
      }
    } catch (error) {
      console.error('Error fetching merch products:', error);
    } finally {
      setLoading(false);
    }
  };

  // Don't render section if no products
  if (!loading && products.length === 0) {
    return null;
  }

  return (
    <section className="section-padding bg-[#0a0b0c]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="inline-block text-[#00d4aa] text-sm font-bold uppercase tracking-widest mb-4">
            Official Merch
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase text-white mb-4">
            GWINS <span className="text-[#00d4aa]">Merch Zone</span>
          </h2>
          <p className="text-white/60 max-w-2xl mx-auto">
            Rep the brand on and off the field. Official GWINS apparel and accessories.
          </p>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-4 border-[#00d4aa] border-t-transparent rounded-full animate-spin mx-auto"></div>
          </div>
        )}

        {/* Products Grid - 3 columns */}
        {!loading && products.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((product) => (
              <a
                key={product.product_id}
                href={product.link || '#'}
                target={product.link ? '_blank' : '_self'}
                rel="noopener noreferrer"
                className="group card-metallic overflow-hidden cursor-pointer block"
              >
                {/* Image */}
                <div className="relative aspect-square overflow-hidden bg-[#1a1c1f]">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/400x400?text=No+Image';
                    }}
                  />
                  
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a0b0c] via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  
                  {/* Quick add */}
                  <Button
                    className="absolute bottom-4 left-1/2 -translate-x-1/2 translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300 bg-[#00d4aa] text-[#0a0b0c] hover:bg-[#33e0bb] rounded-full px-6"
                  >
                    <ShoppingBag className="h-4 w-4 mr-2" />
                    View Product
                  </Button>
                </div>

                {/* Info */}
                <div className="p-4">
                  <h3 className="text-white font-semibold group-hover:text-[#00d4aa] transition-colors">
                    {product.name}
                  </h3>
                  <p className="text-white/80 font-bold mt-1">
                    ${parseFloat(product.price).toFixed(2)}
                  </p>
                </div>
              </a>
            ))}
          </div>
        )}

        {/* View All */}
        {!loading && products.length > 0 && (
          <div className="text-center mt-12">
            <Button
              variant="outline"
              className="border-white/20 text-white hover:bg-white/5 rounded-full px-8 py-6 group"
            >
              Shop All Merch
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};

export default MerchZone;
